<script setup lang="ts">
import {useNotificationStore} from "@/stores/notifications.ts";
import {storeToRefs} from "pinia";
import {ref} from "vue";
import {Icon} from '@iconify/vue';
import {useMusicUploadStore} from "@/stores/useMusicUploadStore.ts";
import {apiClient} from "@/api-client";
import {usePdfExport} from "@/composables/usePdfExport";
import {downloadBlob} from "@/services/download";
import {focusWithoutScroll} from '@/shared/focus';
import {griffschriftDownloadName} from "@/services/fileIdentity";
import MidiPlayer from "@/components/MidiPlayer.vue";
import BlurOnLoad from "@/components/BlurOnLoad.vue";

const {visible = false} = defineProps<{
  visible?: boolean;
}>();

const emit = defineEmits<{
  (e: 'new-conversion'): void;
}>();
const uploadStore = useMusicUploadStore();

// previewFileContent = das konvertierte Griffschrift-MusicXML (inline aus der Convert-Antwort).
// fileInfo = die aktuelle Arbeitsdatei (für den Download-Dateinamen).
const {previewFileContent, fileInfo} = storeToRefs(uploadStore);

const notificationStore = useNotificationStore();
const isRendering = ref(false);
// Separater Loading-State für den clientseitigen (Browser-)PDF-Export – er rendert das Ergebnis
// offscreen in einer eigenen OSMD-Instanz und ist unabhängig vom Server-Render (isRendering).
const isExportingBrowserPdf = ref(false);
const {exportPdfFromXml} = usePdfExport();

// Download-Dateiname aus dem Originalnamen abgeleitet (z. B. `Stück_griffschrift.pdf`).
function downloadName(ext: string): string {
  return griffschriftDownloadName(fileInfo.value?.filename, ext);
}

// Stateless-Download des Konvertierungsergebnisses: MusicXML clientseitig als Blob,
// PDF/PNG/MIDI über POST /api/render aus dem aktuellen Inhalt.
async function handleDownload(type: 'pdf' | 'png' | 'musicxml' | 'midi') {
  const content = previewFileContent.value;
  if (!content) {
    notificationStore.notify(`${type.toUpperCase()} Download nicht verfügbar.`, 'warning');
    return;
  }

  if (type === 'musicxml') {
    downloadBlob(new Blob([content], {type: 'application/xml'}), downloadName('musicxml'));
    return;
  }

  isRendering.value = true;
  try {
    const blob = await apiClient.renderMusicXml(content, type);
    downloadBlob(blob, downloadName(type === 'midi' ? 'mid' : type));
    notificationStore.notify(`${type.toUpperCase()} Download gestartet.`, 'success', 1500);
  } catch (error) {
    console.error(`${type} render failed:`, error);
    notificationStore.notify(`${type.toUpperCase()} Download fehlgeschlagen.`, 'error');
  } finally {
    isRendering.value = false;
  }
}

// Browser-/Frontend-PDF: exportiert das Griffschrift-Ergebnis direkt aus einer OSMD-Render-Instanz
// (jsPDF + svg2pdf.js), ohne Server-Roundtrip. Rendert offscreen, daher unabhängig vom Vorschau-Tab.
async function exportBrowserPdf() {
  const content = previewFileContent.value;
  if (!content) {
    notificationStore.notify('PDF Download nicht verfügbar.', 'warning');
    return;
  }
  isExportingBrowserPdf.value = true;
  try {
    await exportPdfFromXml(content, downloadName('pdf'));
    notificationStore.notify('PDF (Browser) erstellt.', 'success', 1500);
  } catch (error) {
    console.error('Browser-PDF-Export fehlgeschlagen:', error);
    notificationStore.notify('PDF (Browser) fehlgeschlagen.', 'error');
  } finally {
    isExportingBrowserPdf.value = false;
  }
}

async function copyMusicXmlToClipboard() {
  if (!previewFileContent.value) {
    notificationStore.notify('Keine aktive Konvertierung vorhanden.', 'warning');
    return;
  }

  try {
    const musicXmlContent = previewFileContent.value;


    // Copy to clipboard using the modern API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(musicXmlContent);
      notificationStore.notify('MusicXML in die Zwischenablage kopiert.', 'success', 2000);
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement("textarea");
      textArea.value = musicXmlContent;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      focusWithoutScroll(textArea);
      textArea.select();
      const successful = document.execCommand('copy');
      textArea.remove();

      if (successful) {
        notificationStore.notify('MusicXML in die Zwischenablage kopiert.', 'success', 2000);
      } else {
        notificationStore.notify('Kopieren fehlgeschlagen.', 'error');
      }
    }
  } catch (error) {
    console.error('Failed to copy MusicXML:', error);
    notificationStore.notify('Fehler beim Kopieren der MusicXML.', 'error');
  }
}
</script>

<template>
  <div v-if="visible" class=" bg-green-200/20 my-4 rounded-lg shadow-md p-6">
    <BlurOnLoad/>
    <h2 class="text-lg  flex items-center justify-center font-semibold mb-4">
      <Icon icon="fa6-solid:download" class="text-green-600 mr-2"/>
      Download
    </h2>

    <!-- Primary downloads: zwei PDF-Varianten (Server/MuseScore vs. Browser/OSMD) + MusicXML -->
    <div class="flex flex-wrap gap-3 mb-3">
      <button
          class="btn btn-primary flex-1 min-w-[120px]"
          :disabled="isRendering"
          title="Hochwertiger MuseScore-Notensatz (über den Server erzeugt)"
          @click="()=>handleDownload('pdf')"
      >
        <Icon icon="fa6-solid:file-pdf" class="mr-2"/>
        PDF (Server)
      </button>
      <button
          class="btn btn-primary flex-1 min-w-[120px]"
          :disabled="isExportingBrowserPdf"
          title="Genau wie die Vorschau, ohne Server (direkt aus der OSMD-Ansicht)"
          @click="exportBrowserPdf"
      >
        <Icon
            :icon="isExportingBrowserPdf ? 'fa6-solid:spinner' : 'fa6-solid:file-pdf'"
            :class="['mr-2', {'animate-spin': isExportingBrowserPdf}]"
        />
        PDF (Browser)
      </button>
      <button class="btn btn-primary flex-1 min-w-[120px]" @click="()=>handleDownload('musicxml')">
        <Icon icon="fa6-solid:file-code" class="mr-2"/>
        MusicXML
      </button>
    </div>
    <!-- Secondary downloads -->
    <div class="flex flex-wrap gap-2 mb-4">
      <button class="btn btn-secondary flex-1 min-w-[90px]" :disabled="isRendering" @click="()=>handleDownload('png')">
        <Icon icon="fa6-solid:file-image" class="mr-1.5"/>
        PNG
      </button>
      <button
          class="btn btn-secondary flex-1 min-w-[90px]" @click="()=>handleDownload('midi')"
          :disabled="isRendering || !previewFileContent"
      >
        <Icon icon="fa6-solid:music" class="mr-1.5"/>
        MIDI
      </button>
      <button class="btn btn-secondary flex-1 min-w-[90px]" @click="copyMusicXmlToClipboard">
        <Icon icon="fa6-solid:copy" class="mr-1.5"/>
        Kopieren
      </button>
      <button class="btn btn-secondary flex-1 min-w-[130px]" title="Erneut konvertieren – z. B. mit anderen Einstellungen" @click="emit('new-conversion')">
        <Icon icon="fa6-solid:arrow-rotate-right" class="mr-1.5"/>
        Erneut konvertieren
      </button>
    </div>
    <!-- MIDI Player (spielt das konvertierte Ergebnis, stateless via POST /api/midi) -->
    <div v-if="previewFileContent" class="flex justify-center">
      <MidiPlayer :music-xml="previewFileContent"/>
    </div>

  </div>
</template>